/**
 * Custom Redux Hooks
 * 
 * Provides convenient hooks for accessing Redux state and dispatch
 */

import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';

// Re-export standard hooks
export { useSelector } from 'react-redux';

/**
 * Custom hook for typed dispatch
 */
export const useAppDispatch = () => useDispatch();

/**
 * Hook for authentication
 */
export const useAuth = () => {
  const dispatch = useAppDispatch();
  const auth = useSelector((state) => state.auth);

  return {
    ...auth,
    dispatch,
  };
};

/**
 * Hook for complaints
 */
export const useComplaints = () => {
  const dispatch = useAppDispatch();
  const complaints = useSelector((state) => state.complaints);

  return {
    ...complaints,
    dispatch,
  };
};

/**
 * Hook for officers
 */
export const useOfficers = () => {
  const dispatch = useAppDispatch();
  const officers = useSelector((state) => state.officers);

  return {
    ...officers,
    dispatch,
  };
};

/**
 * Hook for settings
 */
export const useSettings = () => {
  const dispatch = useAppDispatch();
  const settings = useSelector((state) => state.settings);

  return {
    ...settings,
    dispatch,
  };
};

/**
 * Hook for notifications
 */
export const useNotifications = () => {
  const dispatch = useAppDispatch();
  const notifications = useSelector((state) => state.notifications);

  return {
    ...notifications,
    dispatch,
  };
};

/**
 * Hook for UI state
 */
export const useUI = () => {
  const dispatch = useAppDispatch();
  const ui = useSelector((state) => state.ui);

  return {
    ...ui,
    dispatch,
  };
};

/**
 * Hook for loading states
 */
export const useLoading = (key) => {
  const loading = useSelector((state) => state.ui.loadingStates[key]);
  return loading || false;
};

/**
 * Hook for toast notifications
 */
export const useToast = () => {
  const dispatch = useAppDispatch();
  const toast = useSelector((state) => state.ui.toast);

  const showToast = useCallback(
    (message, type = 'info') => {
      dispatch({ type: 'ui/showToast', payload: { message, type } });
    },
    [dispatch]
  );

  const hideToast = useCallback(() => {
    dispatch({ type: 'ui/hideToast' });
  }, [dispatch]);

  return {
    ...toast,
    showToast,
    hideToast,
  };
};

/**
 * Hook for modal management
 */
export const useModal = (modalName) => {
  const dispatch = useAppDispatch();
  const isOpen = useSelector((state) => state.ui.modals[modalName]);

  const open = useCallback(() => {
    dispatch({ type: 'ui/openModal', payload: modalName });
  }, [dispatch, modalName]);

  const close = useCallback(() => {
    dispatch({ type: 'ui/closeModal', payload: modalName });
  }, [dispatch, modalName]);

  return {
    isOpen,
    open,
    close,
  };
};

/**
 * Hook for sidebar management
 */
export const useSidebar = () => {
  const dispatch = useAppDispatch();
  const sidebarOpen = useSelector((state) => state.ui.sidebarOpen);

  const toggle = useCallback(() => {
    dispatch({ type: 'ui/toggleSidebar' });
  }, [dispatch]);

  const open = useCallback(() => {
    dispatch({ type: 'ui/setSidebarOpen', payload: true });
  }, [dispatch]);

  const close = useCallback(() => {
    dispatch({ type: 'ui/setSidebarOpen', payload: false });
  }, [dispatch]);

  return {
    isOpen: sidebarOpen,
    toggle,
    open,
    close,
  };
};

export default {
  useAppDispatch,
  useAuth,
  useComplaints,
  useOfficers,
  useSettings,
  useNotifications,
  useUI,
  useLoading,
  useToast,
  useModal,
  useSidebar,
};

